import { getMainVariantImagesFromStore, getNativeVariants } from '@app/ducks/helpers';
import { getDistinctVariantsOrMultivariants, getUniqForProductVariants } from '@app/logic/products';
import { getHasRelatedProducts } from '@app/utils/guards';
import { Product } from '@typings/@centra';
import { uniq } from 'ramda';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
interface Props {
  productDetails: Product.Standard;
  variants: Product.Standard[];
}
export const useProductVariants = ({ productDetails, variants }: Props) => {
  const variantImageSelector = React.useMemo(() => getMainVariantImagesFromStore(productDetails), [productDetails]);
  const variantImages = useSelector(variantImageSelector);

  const uniqByProductVariants = React.useMemo(() => getUniqForProductVariants(productDetails, variants), [productDetails, variants]);

  const distinctOrMultivariants = React.useMemo(
    () => (getHasRelatedProducts(productDetails) ? getDistinctVariantsOrMultivariants([productDetails, ...variants]) : []),
    [productDetails, variants],
  );

  const variantsLength = uniqByProductVariants.length;
  const distinctOrMultivariantsLength = distinctOrMultivariants.length;

  const isSingleProduct = variantsLength === 1;
  const showDistinctVariantRelation = distinctOrMultivariantsLength > 1;
  const showNativeVariants = !showDistinctVariantRelation && variantImages.length > 1 && isSingleProduct;
  const nativeVariants = React.useMemo(() => getNativeVariants(productDetails, productDetails.product), [productDetails]);

  const relatedVariants = React.useMemo(
    () => uniq([...distinctOrMultivariants, ...uniqByProductVariants]),
    [distinctOrMultivariants, uniqByProductVariants],
  );

  return useMemo(
    () => ({
      isSingleProduct,
      nativeVariants,
      relatedVariants,
      showDistinctVariantRelation,
      showNativeVariants,
    }),
    [relatedVariants, nativeVariants, showNativeVariants, isSingleProduct, showDistinctVariantRelation],
  );
};
